import { useState, useCallback, useRef } from 'react';
import { addTaskToConditions, hasConditionForTask, hasAnyConditions, normalizeToV2, removeTaskFromConditions } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { findConnectedNodes } from './utils/graphTraversal';
import { recordUndoableAction } from '../../../../utils/undoRecordBridge';

function capturePositionsForNodeIds(nodeIds, tasks, positionsRef) {
  const { nodePositions, customPositions } = positionsRef.current;
  const out = {};
  nodeIds.forEach((id) => {
    const task = tasks.find((t) => t.id === id);
    const pos = customPositions?.[id] || nodePositions?.[id];
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
      out[id] = { x: pos.x, y: pos.y };
    } else if (task && Number.isFinite(task.x) && Number.isFinite(task.y)) {
      out[id] = { x: task.x, y: task.y };
    }
  });
  return out;
}

function snapshotTaskConditions(task) {
  if (!task) return { unlockConditions: [], dependsOn: [] };
  return {
    unlockConditions: task.unlockConditions
      ? JSON.parse(JSON.stringify(task.unlockConditions))
      : [],
    dependsOn: Array.isArray(task.dependsOn) ? [...task.dependsOn] : [],
  };
}

function resolveConditions(task) {
  let conditions = task?.unlockConditions;
  if (!hasAnyConditions(conditions) && Array.isArray(task?.dependsOn) && task.dependsOn.length > 0) {
    conditions = normalizeToV2(task.dependsOn.map((dep, i) => ({
      taskId: typeof dep === 'string' ? dep : dep?.taskId,
      branch: typeof dep === 'object' ? dep?.branch : undefined,
      operator: i === 0 ? null : 'AND',
    })).filter(c => c.taskId));
  }
  return conditions;
}

/**
 * Hook for managing flowchart connection creation between nodes
 * 
 * Uses coordinate-based target detection for more accurate connection targeting,
 * especially when nodes are close together.
 * 
 * Supports branch connections (yes/no) for decision nodes.
 * 
 * MIGRATION: Now uses onConditionsChange to modify unlockConditions instead of dependsOn.
 * When a connection is created, it adds the source task to the target's first option (AND).
 */
export function useFlowchartConnection({
  tasks,
  nodePositions,
  nodeDimensions,
  customPositions,
  onConditionsChange,
  onDependencyChange, // Keep for backward compatibility during transition
  clearSelection,
  onConnectionComplete, // Called after a connection is successfully created (sourceId, targetId)
}) {
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [connectingTarget, setConnectingTarget] = useState(null);
  const [connectingBranch, setConnectingBranch] = useState(null); // option id for decision nodes
  const [connectingStartPos, setConnectingStartPos] = useState(null); // {x, y} in canvas coords
  const [reconnectingEdge, setReconnectingEdge] = useState(null);
  
  // Ref to store positions/dimensions for use in callbacks without stale closures
  const positionsRef = useRef({ nodePositions, nodeDimensions, customPositions });
  positionsRef.current = { nodePositions, nodeDimensions, customPositions };

  // Start connection drag from node handle
  // branch parameter is optional - used for decision nodes
  // startPos is optional - if provided, the connection line starts from this canvas position
  const handleConnectionDragStart = useCallback((taskId, e, branch = null, startPos = null) => {
    e.stopPropagation();
    e.preventDefault();
    setConnectingFrom(taskId);
    setConnectingTarget(null);
    setConnectingBranch(branch);
    setConnectingStartPos(startPos);
    clearSelection?.();
  }, [clearSelection]);

  /** Drag the target arrowhead to reattach this edge to another node */
  const handleEdgeArrowDragStart = useCallback((edge, e, sourceStartPos = null) => {
    e.stopPropagation();
    e.preventDefault();

    let startPos = sourceStartPos;
    if (!startPos) {
      const { nodePositions, nodeDimensions, customPositions } = positionsRef.current;
      const fromPos = customPositions?.[edge.from] || nodePositions?.[edge.from];
      const fromDims = nodeDimensions?.[edge.from] || { width: 290, height: 150 };
      if (fromPos) {
        startPos = {
          x: fromPos.x + fromDims.width / 2,
          y: fromPos.y + fromDims.height,
        };
      }
    }

    setReconnectingEdge({
      id: edge.id,
      from: edge.from,
      to: edge.to,
      branch: edge.branch ?? null,
    });
    setConnectingFrom(edge.from);
    setConnectingTarget(null);
    setConnectingBranch(edge.branch ?? null);
    setConnectingStartPos(startPos);
  }, []);

  const cancelReconnect = useCallback(() => {
    setReconnectingEdge(null);
    setConnectingFrom(null);
    setConnectingTarget(null);
    setConnectingBranch(null);
    setConnectingStartPos(null);
  }, []);

  const completeReconnect = useCallback((newTargetId) => {
    if (!reconnectingEdge || !newTargetId) {
      cancelReconnect();
      return false;
    }

    const { from, to, branch } = reconnectingEdge;

    if (from === newTargetId) {
      cancelReconnect();
      return false;
    }

    if (to === newTargetId) {
      cancelReconnect();
      return false;
    }

    const oldToTask = tasks.find(t => t.id === to);
    const newToTask = tasks.find(t => t.id === newTargetId);
    if (!oldToTask || !newToTask) {
      cancelReconnect();
      return false;
    }

    const snapshotOldTo = snapshotTaskConditions(oldToTask);
    const snapshotNewTo = snapshotTaskConditions(newToTask);

    const oldToConditions = resolveConditions(oldToTask);
    const updatedOldConditions = removeTaskFromConditions(oldToConditions, from);

    let updatedNewConditions = resolveConditions(newToTask);
    if (!hasConditionForTask(updatedNewConditions, from)) {
      updatedNewConditions = addTaskToConditions(updatedNewConditions, from, branch);
    }

    onConditionsChange?.(to, updatedOldConditions, { skipRecordAction: true });
    onConditionsChange?.(newTargetId, updatedNewConditions, { skipRecordAction: true });

    recordUndoableAction({
      type: 'RECONNECT_EDGE',
      description: 'Reconnect dependency',
      undo: () => {
        onConditionsChange?.(to, snapshotOldTo.unlockConditions, { skipRecordAction: true });
        onConditionsChange?.(newTargetId, snapshotNewTo.unlockConditions, { skipRecordAction: true });
      },
      redo: () => {
        onConditionsChange?.(to, updatedOldConditions, { skipRecordAction: true });
        onConditionsChange?.(newTargetId, updatedNewConditions, { skipRecordAction: true });
      },
    });

    const affected = findConnectedNodes(new Set([from, to, newTargetId]), tasks);
    const originalPositions = capturePositionsForNodeIds([...affected], tasks, positionsRef);
    onConnectionComplete?.(from, newTargetId, { originalPositions });

    cancelReconnect();
    return true;
  }, [reconnectingEdge, tasks, onConditionsChange, onConnectionComplete, cancelReconnect]);

  // Find which node (if any) is under the given canvas coordinates
  // Returns the node with the smallest area that contains the point (most specific match)
  const findNodeAtPosition = useCallback((canvasX, canvasY) => {
    const { nodePositions, nodeDimensions, customPositions } = positionsRef.current;
    const skipTargetId = reconnectingEdge?.to ?? null;
    
    let bestMatch = null;
    let bestArea = Infinity;
    
    for (const task of tasks) {
      if (task.id === connectingFrom) continue;
      if (skipTargetId && task.id === skipTargetId) continue;
      
      const pos = customPositions?.[task.id] || nodePositions?.[task.id];
      if (!pos) continue;
      
      const dims = nodeDimensions?.[task.id] || { width: 290, height: 150 };
      
      const isInside = canvasX >= pos.x && 
          canvasX <= pos.x + dims.width &&
          canvasY >= pos.y && 
          canvasY <= pos.y + dims.height;
      
      // Check if point is inside node bounds
      if (isInside) {
        // If multiple nodes contain the point (overlapping), pick the smallest one
        const area = dims.width * dims.height;
        if (area < bestArea) {
          bestArea = area;
          bestMatch = task.id;
        }
      }
    }
    
    return bestMatch;
  }, [tasks, connectingFrom, reconnectingEdge]);

  // Update connection target based on mouse position (called during mouse move)
  const updateConnectionTarget = useCallback((canvasX, canvasY) => {
    if (!connectingFrom) return;

    const targetId = findNodeAtPosition(canvasX, canvasY);
    setConnectingTarget((prev) => (prev === targetId ? prev : targetId));
  }, [connectingFrom, findNodeAtPosition]);

  // Handle hovering over a potential connection target (DOM event fallback)
  const handleConnectionTargetEnter = useCallback((taskId) => {
    if (connectingFrom && connectingFrom !== taskId) {
      setConnectingTarget(taskId);
    }
  }, [connectingFrom]);

  const handleConnectionTargetLeave = useCallback(() => {
    // Only clear if we're not using coordinate-based detection
    // The coordinate-based detection will handle this more accurately
  }, []);

  /**
   * Add a task to unlockConditions (as AND in the first block).
   * Handles v1, v2, and dependsOn migration automatically.
   */
  const addToConditions = useCallback((targetTaskId, sourceTaskId, branch = null, options = {}) => {
    const task = tasks.find(t => t.id === targetTaskId);
    if (!task) return null;

    let conditions = task.unlockConditions;
    if (!hasAnyConditions(conditions) && Array.isArray(task.dependsOn) && task.dependsOn.length > 0) {
      conditions = normalizeToV2(task.dependsOn.map((dep, i) => ({
        taskId: typeof dep === 'string' ? dep : dep?.taskId,
        branch: typeof dep === 'object' ? dep?.branch : undefined,
        operator: i === 0 ? null : 'AND',
      })).filter(c => c.taskId));
    }

    if (hasConditionForTask(conditions, sourceTaskId)) return null;

    const updated = addTaskToConditions(conditions, sourceTaskId, branch);
    return onConditionsChange?.(targetTaskId, updated, options) ?? null;
  }, [tasks, onConditionsChange]);

  // Complete connection (on target drop or click)
  // For decision nodes, includes branch info (yes/no) in the dependency
  const completeConnection = useCallback((targetId) => {
    if (reconnectingEdge) {
      return completeReconnect(targetId);
    }

    if (!connectingFrom || connectingFrom === targetId) {
      setConnectingFrom(null);
      setConnectingTarget(null);
      setConnectingBranch(null);
      return false;
    }

    const task = tasks.find(t => t.id === targetId);
    
    const existsInConditions = hasConditionForTask(task?.unlockConditions, connectingFrom);
    
    const sourceId = connectingFrom; // Capture before clearing
    
    if (!existsInConditions) {
      const affected = findConnectedNodes(new Set([sourceId, targetId]), tasks);
      const originalPositions = capturePositionsForNodeIds([...affected], tasks, positionsRef);
      const undoDefer = addToConditions(targetId, sourceId, connectingBranch, { skipRecordAction: true });
      onConnectionComplete?.(sourceId, targetId, { undoDefer, originalPositions });
    }
    
    setConnectingFrom(null);
    setConnectingTarget(null);
    setConnectingBranch(null);
    
    return true;
  }, [reconnectingEdge, completeReconnect, connectingFrom, connectingBranch, tasks, addToConditions, onConnectionComplete]);

  // Complete connection if we have a valid target (on mouse up)
  const completeConnectionIfValid = useCallback(() => {
    if (reconnectingEdge) {
      if (connectingTarget) {
        completeReconnect(connectingTarget);
        return true;
      }
      cancelReconnect();
      return false;
    }

    if (connectingFrom && connectingTarget) {
      completeConnection(connectingTarget);
      return true;
    }
    setConnectingFrom(null);
    setConnectingTarget(null);
    setConnectingBranch(null);
    setConnectingStartPos(null);
    return false;
  }, [reconnectingEdge, connectingFrom, connectingTarget, completeConnection, completeReconnect, cancelReconnect]);

  // Cancel connection
  const cancelConnection = useCallback(() => {
    if (reconnectingEdge) {
      cancelReconnect();
      return;
    }
    setConnectingFrom(null);
    setConnectingTarget(null);
    setConnectingBranch(null);
    setConnectingStartPos(null);
  }, [reconnectingEdge, cancelReconnect]);

  return {
    // State
    connectingFrom,
    connectingTarget,
    connectingBranch,
    connectingStartPos,
    reconnectingEdge,
    isConnecting: connectingFrom !== null,
    
    // Handlers
    handleConnectionDragStart,
    handleEdgeArrowDragStart,
    handleConnectionTargetEnter,
    handleConnectionTargetLeave,
    updateConnectionTarget,
    completeConnection,
    completeConnectionIfValid,
    cancelConnection,
  };
}
